import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useI18n } from './useI18n';
import { useAdDevice } from './useAdDevice';
import { SIMIMOVIES_MANAGED_ADS_ENABLED } from '../config/ads';
import { fetchAdPlacement } from '../store/actions/adActions';

/**
 * Fetches managed ad placements for a page. Pass a stable `requests` array (e.g. from useMemo(() => [...], []))
 * so the effect does not re-run every render.
 *
 * @param {string} page - Targeting page key (e.g. 'explore', 'categories')
 * @param {Array<{ placement: string, limit?: number }>} requests
 * @returns {{ adDomain: string, adLocale: string, adDevice: string }}
 */
export function useFetchManagedPlacements(page, requests) {
  const dispatch = useDispatch();
  const { locale } = useI18n();
  const adDevice = useAdDevice();
  const adDomain = useMemo(() => (typeof window !== 'undefined' ? window.location.hostname : ''), []);
  const adLocale = (locale || 'en').toLowerCase();

  const reqKey = useMemo(
    () => (requests || []).map((r) => `${r.placement}:${r.limit ?? 1}`).join('|'),
    [requests],
  );

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !reqKey) return;
    const list = requests || [];
    if (!list.length) return;
    list.forEach(({ placement, limit = 1 }) => {
      dispatch(
        fetchAdPlacement({
          page,
          placement,
          params: { page, placement, limit, device: adDevice, domain: adDomain, locale: adLocale },
        }),
      );
    });
  }, [dispatch, page, reqKey, adDevice, adDomain, adLocale]);

  return { adDomain, adLocale, adDevice };
}
